import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { apiError, downloadPdfFile } from "../lib/api";
import { track } from "../lib/analytics";
import RoadmapView from "../components/RoadmapView";
import { useAuth } from "../context/AuthContext";
import { PencilLine } from "lucide-react";
import { toast } from "sonner";
import ChatPanel from "../components/ChatPanel";
import { useVerifyGate } from "../components/VerifyGate";

export default function ProjectPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const changes = location.state?.changes;
  const { ensureVerified, gate } = useVerifyGate();

  const doDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadPdfFile(`/projects/${projectId}/pdf`, `RegNav - ${project?.name || "roadmap"}.pdf`);
      toast.success("PDF downloaded — a Solarian-branded report");
      track("pdf_downloaded", { source: "project" });
    } catch {
      toast.error("PDF couldn't be generated — try again", { action: { label: "Retry", onClick: doDownloadPdf } });
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadPdf = () => ensureVerified(doDownloadPdf);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/projects/${projectId}`)}`);
      return;
    }
    api.get(`/projects/${projectId}`).then(({ data }) => setProject(data)).catch((e) => setError(apiError(e)));
  }, [projectId, user, loading, navigate]);

  if (error) return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center">
      <p className="font-serif text-xl text-[#0B1F3A]">{error}</p>
      <button onClick={() => navigate("/projects")} className="mt-4 bg-[#1D4ED8] text-white rounded-md px-5 py-2.5 text-sm font-medium">Back to my projects</button>
    </div>
  );
  if (!project) return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-4">
      <div className="h-48 bg-[#0B1F3A]/10 rounded-xl animate-pulse" />
      <div className="h-64 bg-[#0B1F3A]/10 rounded-xl animate-pulse" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {changes && (changes.added?.length > 0 || changes.removed?.length > 0) && (
        <div className="mb-4 bg-white border-2 border-[#059669] rounded-lg px-4 py-3 text-sm" data-testid="what-changed-note">
          <p className="font-semibold text-[#0B1F3A]">What changed after your edit</p>
          {changes.added?.length > 0 && <p className="text-[#0B1F3A] mt-1">+{changes.added.length} approval{changes.added.length > 1 ? "s" : ""} added ({changes.added.join(", ")})</p>}
          {changes.removed?.length > 0 && <p className="text-[#475569] mt-0.5">−{changes.removed.length} removed ({changes.removed.join(", ")})</p>}
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <button onClick={() => navigate(`/wizard?project=${projectId}`)} className="inline-flex items-center gap-2 border border-[#0B1F3A]/30 text-[#0B1F3A] rounded-md px-4 py-2 text-sm font-medium hover:bg-[#0B1F3A]/5" data-testid="edit-regenerate-btn">
          <PencilLine size={15} /> Edit inputs & regenerate
        </button>
      </div>
      <RoadmapView roadmap={project.roadmap} name={project.name} projectId={projectId} onDownloadPdf={downloadPdf} pdfBusy={pdfBusy} />
      <div className="mt-6"><ChatPanel contextId={projectId} /></div>
      {gate}
    </div>
  );
}
